import { z } from 'zod';

import {
	DEVICE_ID_COOKIE_NAME,
	DeviceType,
	REFRESH_TOKEN_COOKIE_NAME,
} from '../../constants/auth.constants';
import { emailOpenApiDescription } from '../../constants/schemas.constants';
import {
	PLAIN_TEXT_WITH_EMOJI_ERROR_MESSAGE,
	PLAIN_TEXT_WITH_EMOJI_REGEX,
	UUID_ERROR_MESSAGE,
} from '../../constants/validation.constants';
import { requiredString, requiredUuid } from '../base.schema';

export const DeviceTypeSchema = z.enum(DeviceType).meta({
	description: 'Type of device being used by the user',
	example: DeviceType.MOBILE,
});

export const loginSchemaName = 'LoginRequest';

export const loginSchema = z
	.object({
		email: requiredString('schemaAttributes.email').meta(
			emailOpenApiDescription
		),
		password: requiredString('schemaAttributes.password').meta({
			description: 'User’s plain text password',
			example: 'P@ssw0rd!',
		}),
		deviceType: DeviceTypeSchema,
		deviceName: requiredString('schemaAttributes.deviceName')
			.regex(
				PLAIN_TEXT_WITH_EMOJI_REGEX,
				PLAIN_TEXT_WITH_EMOJI_ERROR_MESSAGE
			)
			.meta({
				description:
					'Friendly name for the device, can contain text and emojis.',
				example: 'iPhone',
			}),
	})
	.strip()
	.meta({
		ref: loginSchemaName,
		description: 'Schema for user login payload.',
	});

export type LoginSchemaType = z.infer<typeof loginSchema>;

export const loginRequestCookiesSchemaName = 'LoginRequestCookies';

export const loginRequestCookiesSchema = z
	.object({
		[DEVICE_ID_COOKIE_NAME]: z.uuid(UUID_ERROR_MESSAGE).optional().meta({
			description: 'UUID identifying the device',
			example: '550e8400-e29b-41d4-a716-446655440000',
		}),
	})
	.strip()
	.meta({
		ref: loginRequestCookiesSchemaName,
		description: 'Schema for cookies refresh token payload.',
	});

export type LoginRequestCookiesSchemaType = z.infer<
	typeof loginRequestCookiesSchema
>;

export const googleAuthSchemaName = 'GoogleAuth';

export const googleAuthSchema = z
	.object({
		authCode: requiredString('schemaAttributes.googleAuthCode').meta({
			description: 'Authorization code returned by Google OAuth2 flow',
			example: '4/0AX4XfWhgS-xyz123',
		}),
		deviceType: DeviceTypeSchema,
		deviceName: requiredString('schemaAttributes.deviceName')
			.regex(
				PLAIN_TEXT_WITH_EMOJI_REGEX,
				PLAIN_TEXT_WITH_EMOJI_ERROR_MESSAGE
			)
			.meta({
				description:
					'Friendly name for the device, can contain text and emojis.',
				example: 'iPhone',
			}),
	})
	.strip()
	.meta({
		ref: googleAuthSchemaName,
		description: 'Schema for Google OAuth2 login payload.',
	});

export type GoogleAuthSchemaType = z.infer<typeof googleAuthSchema>;

export const refreshTokenCookiesSchemaRef = 'RefreshTokenRequest';

export const refreshTokenCookiesSchema = z
	.object({
		[REFRESH_TOKEN_COOKIE_NAME]: z
			.jwt()
			.nonempty({
				message: 'schemaAttributes.refreshToken:zodErrors.isRequired',
			})
			.meta({
				description: 'Refresh token used to obtain new access token',
				example:
					'efh8923h2ofhwcoinq2f8092qh4f289gheqfnoq24389gefhn2318q09wf0hio2nhq82923fc23qf',
			}),
	})
	.strip()
	.meta({
		ref: refreshTokenCookiesSchemaRef,
		description: 'Schema for cookies refresh token payload.',
	});

export type RefreshTokenCookiesSchemaType = z.infer<
	typeof refreshTokenCookiesSchema
>;

export const logoutCookiesSchemaRef = 'Logout';

export const logoutCookiesSchema = z
	.object({
		[REFRESH_TOKEN_COOKIE_NAME]: z
			.jwt()
			.nonempty({
				message: 'schemaAttributes.refreshToken:zodErrors.isRequired',
			})
			.meta({
				description: 'Refresh token used to obtain new access token',
				example:
					'efh8923h2ofhwcoinq2f8092qh4f289gheqfnoq24389gefhn2318q09wf0hio2nhq82923fc23qf',
			}),
	})
	.strip()
	.meta({
		ref: logoutCookiesSchemaRef,
		description: 'Schema for cookies refresh token payload.',
	});

export type LogoutCookiesSchemaType = z.infer<typeof logoutCookiesSchema>;

export const logoutDeviceRequestParamsSchemaRef = 'logoutDeviceRequestParams';

export const logoutDeviceRequestParamsSchema = z
	.object({
		id: requiredUuid('schemaAttributes.deviceId').meta({
			description: 'Device ID to be logged out',
			example: '1341343-31241324132-132413',
		}),
	})
	.meta({
		description: 'Schema for logging out a device',
		ref: logoutDeviceRequestParamsSchemaRef,
	})
	.strict();

export type LogoutDeviceRequestParamsSchemaType = z.infer<
	typeof logoutDeviceRequestParamsSchema
>;
